/**
 * Grid Data Adapter
 *
 * Converts the new GridData structure to the existing Layout structure.
 * This adapter allows gradual migration by maintaining backward compatibility
 * with the existing rendering layer.
 */

import {type Layout} from '../../Component';
import {type Tile} from '../../tiles/types';
import {type GridCell, type GridData} from '../types';

/**
 * Converts GridData to the existing Layout structure.
 *
 * This is a temporary adapter that allows the new layout architecture
 * to work with the existing rendering components. Eventually, the rendering
 * layer will be updated to consume GridData directly.
 *
 * @param gridData - New layout structure
 * @returns Layout compatible with existing rendering components
 */
export function adaptGridDataToLayout(gridData: GridData): Layout {
  // Convert rows/cells to flat tile list
  const tiles: Tile[] = [];

  for (const row of gridData.rows) {
    for (const cell of row.cells) {
      const tile = adaptCellToTile(cell);
      tiles.push(tile);
    }
  }

  // Determine axis data based on layout type
  let galleryAxisData: number[] | null | undefined;
  let gridAxisData: {x: number[]; y: number[]} | null | undefined;

  if (gridData.metadata.layoutType === 'gallery') {
    // Gallery mode: use actual axis values from metadata if available
    // Fall back to row indices if axis data is not provided
    galleryAxisData =
      gridData.metadata.axisData?.y ?? gridData.rows.map((_, i) => i);
    gridAxisData = null;
  } else if (gridData.metadata.layoutType === 'grid') {
    // Grid mode: provide x and y axis data
    galleryAxisData = null;
    if (
      gridData.metadata.axisData &&
      gridData.metadata.axisData.x &&
      gridData.metadata.axisData.y
    ) {
      gridAxisData = {
        x: gridData.metadata.axisData.x,
        y: gridData.metadata.axisData.y,
      };
    } else {
      gridAxisData = null;
    }
  } else if (gridData.metadata.layoutType === 'compare') {
    // Compare mode: axis data represents columns (x-axis) and rows (y-axis)
    // X-axis: column indices, Y-axis: single row
    galleryAxisData = null;
    if (
      gridData.metadata.axisData &&
      gridData.metadata.axisData.x &&
      gridData.metadata.axisData.y
    ) {
      gridAxisData = {
        x: gridData.metadata.axisData.x,
        y: gridData.metadata.axisData.y,
      };
    } else {
      // Fallback: generate column indices if axis data not provided
      // In compare mode, each row represents a column, so row count = column count
      const columnCount = gridData.rows.length;
      gridAxisData = {
        x: Array.from({length: columnCount}, (_, i) => i),
        y: [0],
      };
    }
  }

  return {
    tiles,
    galleryAxisData,
    gridAxisData,
  };
}

/**
 * Converts a GridCell to a Tile (NonEmptyTile or EmptyTile).
 *
 * @param cell - GridCell from new layout structure
 * @returns Tile compatible with existing rendering
 */
function adaptCellToTile(cell: GridCell): Tile {
  // Check if this is an empty cell placeholder
  if (cell.isEmpty) {
    return {
      mediaKey: cell.mediaKey,
      isEmpty: true,
    } as Tile;
  }

  // At this point, TypeScript knows cell is CompleteGridCell
  // Use tileSpec if available (it has all the rendering info)
  if (cell.tileSpec) {
    return {
      mediaKey: cell.tileSpec.mediaKey,
      mediaSliderKey: cell.tileSpec.mediaSliderKey,
      mediaSliderKeyValue: cell.tileSpec.mediaSliderKeyValue,
      mediaStep: cell.tileSpec.mediaStep, // Use tileSpec.mediaStep (the _step number), not cell.step (display value)
      mediaIndex: cell.mediaIndex,
      runSignature: cell.tileSpec.runSignature,
      run: cell.tileSpec.run as any, // Type assertion needed for compatibility
      isEmpty: false,
    };
  }

  // Fallback: construct from cell data
  return {
    mediaKey: cell.mediaKey,
    mediaStep: cell.step,
    mediaIndex: cell.mediaIndex,
    mediaSliderKeyValue: cell.step,
    runSignature: {
      entityName: cell.run.entityName,
      projectName: cell.run.projectName,
      runName: cell.run.name,
    },
    run: cell.run as any, // Type assertion needed for compatibility
    isEmpty: false,
  };
}

/**
 * Creates an empty Layout structure.
 *
 * @returns Empty layout
 */
export function createEmptyLayout(): Layout {
  return {
    tiles: [],
    galleryAxisData: null,
    gridAxisData: null,
  };
}
